// SPDX-License-Identifier: AGPL-3.0-or-later

import {ShopPayableFields} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import {SnowflakeStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

export const ShopBillingMode = z
	.enum(['one_time', 'recurring'])
	.describe('one_time: a prepaid package; recurring: a Paddle subscription that renews until canceled');
export type ShopBillingMode = z.infer<typeof ShopBillingMode>;

export const PaddleSubscriptionInterval = z.union([z.literal(1), z.literal(3), z.literal(12)]);
export type PaddleSubscriptionInterval = z.infer<typeof PaddleSubscriptionInterval>;

// How many boosts one boost subscription holds: Paddle's price quantity range ends at 100; the account's
// max_purchased_boost_slots usually ends it earlier.
export const PADDLE_BOOST_MAX_QUANTITY = 100;
export const PaddleBoostQuantity = z.number().int().min(1).max(PADDLE_BOOST_MAX_QUANTITY);

// The recurring terms the buyer accepts; a change means a new version and a fresh consent.
export const SHOP_RECURRING_TERMS_VERSION = '2026-10-25';

export const RecurringConsent = z
	.object({
		accepted: z.literal(true),
		terms_version: z.string().min(1).max(64),
		amount_minor: z.number().int().positive().max(2_147_483_647),
		currency: z.enum(['USD', 'TRY']),
		interval_months: PaddleSubscriptionInterval,
		trial_days: z
			.number()
			.int()
			.min(1)
			.max(30)
			.nullable()
			.optional()
			.describe("The offer's free days before the first charge (recurring_quote.trial_days); absent or null for none"),
		quantity: PaddleBoostQuantity.optional().describe(
			'A boost subscription only: how many boosts amount_minor pays for each period; absent for Plus',
		),
	})
	.describe(
		'The recurring offer exactly as the buyer saw and accepted it; the server prices it again and refuses a mismatch',
	);
export type RecurringConsent = z.infer<typeof RecurringConsent>;

export const RecurringQuote = RecurringConsent.omit({accepted: true})
	.extend({
		billing_mode: z.literal('recurring'),
		...ShopPayableFields,
		trial_days: z
			.number()
			.int()
			.nullable()
			.optional()
			.describe('Free days before the first charge this account would get now; null for none'),
	})
	.describe('What every period charges: the full amount and currency each interval_months, at the list price');
export type RecurringQuote = z.infer<typeof RecurringQuote>;

export const RecurringEligibility = z.object({
	eligible: z.boolean(),
	reason: z
		.enum([
			'sales_closed',
			'region_unavailable',
			'prepaid_active',
			'existing_subscription',
			'checkout_pending',
			'visionary',
			'purchase_disabled',
		])
		.nullable(),
	available_after: z.iso
		.datetime()
		.nullable()
		.describe('prepaid_active and existing_subscription: when the current Plus time ends'),
});
export type RecurringEligibility = z.infer<typeof RecurringEligibility>;

export const PaddleSubscriptionStatus = z.enum(['pending', 'active', 'past_due', 'paused', 'canceled', 'needs_review']);
export type PaddleSubscriptionStatus = z.infer<typeof PaddleSubscriptionStatus>;

export const PaddleSubscriptionState = z.object({
	status: PaddleSubscriptionStatus.describe(
		'pending: the first payment is not confirmed yet; past_due: a renewal payment failed and Paddle retries it; needs_review: support has to look at it',
	),
	interval_months: PaddleSubscriptionInterval,
	amount_minor: z.number().int().positive().describe('What each period charges, in the currency'),
	currency: z.enum(['USD', 'TRY']),
	paid_until: z.iso.datetime().nullable().describe('End of the last paid period; Plus lasts until then'),
	next_billed_at: z.iso.datetime().nullable().describe('When Paddle charges next; null once canceled'),
	cancel_at: z.iso.datetime().nullable().describe('A cancellation scheduled for the end of the paid period'),
	can_cancel: z.boolean().describe('POST /paddle/subscription/cancel is open'),
	can_reactivate: z.boolean().describe('POST /paddle/subscription/reactivate is open: a cancellation is scheduled'),
	can_update_payment_method: z.boolean().describe('POST /paddle/subscription/payment-method is open'),
});
export type PaddleSubscriptionState = z.infer<typeof PaddleSubscriptionState>;

export const PaddleAdminSubscriptionResponse = z.object({
	subscription_id: z.string(),
	customer_id: z.string(),
	order_id: SnowflakeStringType.describe('The shop order of the first payment'),
	status: PaddleSubscriptionStatus,
	interval_months: PaddleSubscriptionInterval,
	amount_minor: z.number().int(),
	currency: z.enum(['USD', 'TRY']),
	paid_until: z.iso.datetime().nullable(),
	next_billed_at: z.iso.datetime().nullable(),
	cancel_at: z.iso.datetime().nullable(),
	pending_cancel: z
		.string()
		.nullable()
		.describe(
			'Why Micup still has to cancel it at Paddle (refund, chargeback, account deletion); null when nothing waits',
		),
	review_reason: z.string().nullable().describe('Why it needs support; null when it does not'),
	created_at: z.iso.datetime(),
	quantity: z
		.number()
		.int()
		.positive()
		.optional()
		.describe('A boost subscription: the boosts it renews; amount_minor is one boost. Absent for Plus'),
});
export type PaddleAdminSubscriptionResponse = z.infer<typeof PaddleAdminSubscriptionResponse>;

export const PaddleAdminUserSubscriptionResponse = z.object({
	subscription: PaddleAdminSubscriptionResponse.nullable().describe("The account's latest Paddle subscription"),
	boost_subscription: PaddleAdminSubscriptionResponse.nullable()
		.optional()
		.describe("The account's latest Paddle boost subscription"),
});
export type PaddleAdminUserSubscriptionResponse = z.infer<typeof PaddleAdminUserSubscriptionResponse>;

export const PaddleBoostSubscriptionState = PaddleSubscriptionState.extend({
	interval_months: z.literal(1),
	amount_minor: z.number().int().positive().describe('What each month charges for all its boosts, in the currency'),
	quantity: PaddleBoostQuantity.describe('Boosts the subscription renews; the next charge pays for this many'),
	unit_amount_minor: z.number().int().positive().describe('What one boost costs each month'),
	can_change_quantity: z.boolean().describe('PATCH /paddle/boost-subscription is open'),
});
export type PaddleBoostSubscriptionState = z.infer<typeof PaddleBoostSubscriptionState>;

export const PaddleBoostSubscriptionResponse = z.object({
	subscription: PaddleBoostSubscriptionState.nullable().describe("The account's boost subscription; null for none"),
	max_quantity: z
		.number()
		.int()
		.min(0)
		.max(PADDLE_BOOST_MAX_QUANTITY)
		.describe(
			'The most boosts the subscription can hold now: the account limit less the bought boosts outside it; 0 when it can hold none',
		),
});
export type PaddleBoostSubscriptionResponse = z.infer<typeof PaddleBoostSubscriptionResponse>;

export const PaddleBoostQuantityRequest = z.object({
	quantity: PaddleBoostQuantity.describe('How many boosts the subscription should hold'),
	charge_now: z
		.boolean()
		.optional()
		.describe(
			'A change only: whether the preview the buyer confirmed charged at once (charge_now_minor not null); a change that would now do otherwise is refused',
		),
});
export type PaddleBoostQuantityRequest = z.infer<typeof PaddleBoostQuantityRequest>;

export const PaddleBoostPreviewResponse = z.object({
	quantity: PaddleBoostQuantity,
	currency: z.enum(['USD', 'TRY']),
	charge_now_minor: z
		.number()
		.int()
		.min(0)
		.nullable()
		.describe("What Paddle charges at once for the added boosts' remaining days, tax included; null when nothing"),
	recurring_amount_minor: z.number().int().positive().describe('What each month charges from the next renewal'),
	next_billed_at: z.iso.datetime().nullable(),
});
export type PaddleBoostPreviewResponse = z.infer<typeof PaddleBoostPreviewResponse>;
